import React from 'react';
import { useCreateCategoryMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import type { CategoryFormSubmit } from './components/categoryFormModal/useCategoryForm';

/**
 * Creating a category — the one definition, shared by the categories page and
 * the Control Center's "Add category".
 *
 * See `@/pages/brands/useCreateBrand` for why the create path lives in a hook
 * of its own. Only creation is here: the edit path normalizes the display image
 * URL back to a server-relative one and can upload before it writes, neither of
 * which a create does or the launcher ever reaches.
 */
export const useCreateCategory = () => {
    const createMutation = useCreateCategoryMutation();

    const create = React.useCallback(
        async (input: CategoryFormSubmit): Promise<CreateOutcome> => {
            const result = await createMutation.mutateAsync({
                name: input.name,
                slug: input.slug,
                parentId: input.parentId,
                imageUpload: input.imageUpload,
            });

            return result.success
                ? { ok: true }
                : {
                      ok: false,
                      message:
                          result.error.message ?? 'Failed to create category.',
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
